import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {PipelineEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Badge} from "../../badge/Badge/Badge";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {Anchor} from "@mantine/core";
import {Link} from "react-router-dom";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {usePipelineDataTableFilter} from "./usePipelineDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: PipelineEntity[] = []

export type PipelineDataTableProps = EntityDataTableProps<PipelineEntity>

export const PipelineDataTable = (props: PipelineDataTableProps) => {
  const {
    records: pipelines = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = usePipelineDataTableFilter({
    records: pipelines
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      idAccessor={(p) => p.id}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "name"),
          title: t("table.entity.pipeline.column.name", "Name"),
          accessor: "name",
          width: 100,
          sortable: true,
          render: ({id, name}) => (
            <Anchor component={Link} to={`/pipelines/profile?id=${id}`}>
              {name}
            </Anchor>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "category"),
          title: t("table.entity.pipeline.column.category", "Category"),
          accessor: "category",
          width: 100,
          sortable: true,
          render: ({category}) => {
            return t(category.displayValue.id, category.displayValue.defaultMessage)
          }
        },
        {
          ...getColumnFilterProps(filterConfig, "subCategory"),
          title: t("table.entity.pipeline.column.freighttypecategory", "Sub Category"),
          accessor: "freightTypeCategory",
          width: 100,
          sortable: true,
        },
        {
          title: t("table.entity.pipeline.column.inactive", "Inactive"),
          accessor: "inactiveDttm",
          width: 100,
          sortable: true,
          render: ({inactiveDttm}) => {
            if (inactiveDttm) {
              return (
                <Badge color="red">
                  <FormattedDateTime date={inactiveDttm}/>
                </Badge>
              )
            }
            return undefined
          }
        },
        {
          title: t("table.entity.pipeline.column.temporary", "Temporary"),
          accessor: "temporary",
          width: 100,
          sortable: true,
          render: ({temporary}) => {
            if (temporary) {
              return (
                <Badge color="orange">
                  {t("common.shared.temporary", "Temporary")}
                </Badge>
              )
            }
            return undefined
          }
        },
        {
          ...getColumnFilterProps(filterConfig, "allocation"),
          title: t("table.entity.pipeline.column.allocation", "Allocation"),
          accessor: "allocationType.code",
          width: 100,
          sortable: true,
          render: ({allocationType}) => {
            return t(allocationType.displayValue.id, allocationType.displayValue.defaultMessage)
          }
        },
        {
          ...getColumnFilterProps(filterConfig, "company"),
          title: t("table.entity.pipeline.column.company", "Company"),
          accessor: "company",
          width: 100,
          sortable: true,
          render: ({companyName}) => (
            <DefaultText value={companyName}/>
          ),
        },
      ]}
    />
  )
}
